import Head from 'next/head';
import React, { useState, useEffect, useMemo } from 'react';
import Router from 'next/router';

// Component
import Breadcrumb from 'components/Breadcrumb';
import Collapsible from 'components/CourseDescription/Collapsible';
import CourseData from 'components/CourseDescription/CourseData';
import RecommendedProducts from 'components/ProductDescription/RecommendedProducts';
import ModalCheckout from 'components/CourseDescription/ModalCheckout';
import { Spinner } from 'react-bootstrap';

// Utils
import { useImageHandler } from 'utils/custom_hooks';
import { readingTimeGenerator, languageGenerator, dateGenerator, typeGenerator } from 'utils/product_description';
import { synchronizedSingleCartData } from 'utils/manipulate_array';
import { generateCoverImageUrl } from 'utils/generate_cover_image_url';

import { truncateText } from 'utils/helper';
import Button from 'components/Button';
import { BUTTON_TYPE } from 'constants/button';
import { Heading, SubHeading, TextBody } from 'components/Typography/General';
import { FONT_TYPES } from 'variables/font_groups';
import { isMobile } from 'react-device-detect';
import { DETAIL_TITLE } from 'constants/courseDescription';
import { trackAddToCart, trackCtaClick, trackRemoveFromCart } from 'lib/analytics';

const { getProductDetail, getProductList } = require('services/products');

export async function getServerSideProps(context) {
  const { req, query } = context;
  const { productId } = query;

  const productDetail = await getProductDetail(productId);
  const baseUrl = `${req.protocol || 'https'}://${req.headers.host}`;

  if (!productDetail) {
    return {
      notFound: true,
    };
  }

  const isBaseDomain = baseUrl === process.env.NEXT_PUBLIC_BASE_PATH;
  const _id = productDetail._id;
  const productRecommendation = await getProductList({
    notIn: _id,
    random: true,
    provider: isBaseDomain ? '' : `&provider=${productDetail.provider.code}`,
  });

  return {
    props: {
      productDetail,
      productRecommendation,
      url: `${baseUrl}${req.url}`,
    },
  };
}

const ProductDescription = (props) => {
  const { productDetail, productRecommendation, url } = props;

  const {
    center,
    description,
    discount,
    price,
    duration,
    image,
    language,
    productId,
    provider,
    purchaseOption,
    referenceNumber,
    releasedAt,
    title,
    totalPages,
    type,
  } = productDetail;

  const memoizedOptionList = useMemo(
    () => [provider.logo, provider.companyLogo],
    [provider.logo, provider.companyLogo]
  );
  const { currentImage, handleIsImageAvailable, handleImageToDefault } = useImageHandler({
    optionList: memoizedOptionList,
    data: productDetail,
  });

  const productLanguage = languageGenerator(language);
  const productReadingTime = readingTimeGenerator(duration);
  const productType = typeGenerator(type);
  const productReleaseDate = dateGenerator(releasedAt);

  const [isLoading, setIsLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [currentSelect, SetCurrentSelect] = useState('');
  const [recommendedExpand, setRecommendedExpand] = useState(false);
  const [openModalCheckout, setOpenModalCheckout] = useState(false);

  const isAddedToCart = cart.filter((data_cart) => data_cart.itemId == productId).length !== 0;

  const handleAddToCart = (data) => {
    const newCart = synchronizedSingleCartData({ item: data, isProduct: true });

    setIsLoading(true);
    let list_cart = [...cart];
    let new_data = [];
    const price = parseFloat(data?.price?.$numberDecimal ? data?.price?.$numberDecimal : data?.price || 0);

    if (list_cart.filter((cart) => cart.itemId == newCart.itemId).length !== 0) {
      new_data = list_cart.filter((cart) => cart.itemId !== newCart.itemId);
      trackCtaClick({
        page_type: 'product_detail',
        cta_label: 'remove_from_cart',
        location: 'product_course_detail',
        course_id: data.productId,
      });

      trackRemoveFromCart({
        page_type: 'product_detail',
        currency: 'USD',
        value: price * -1,
        items: [
          {
            item_id: data.productId,
            item_name: data.name,
            item_category: data.categoryName || 'unknown',
            item_variant: 'course',
            price,
            quantity: 0,
          },
        ],
        course_id: data._id,
      });
      Router.reload(window.location.pathname);
    } else {
      trackCtaClick({
        page_type: 'product_detail',
        cta_label: 'add_to_cart',
        location: 'product_course_detail',
        course_id: data.productId,
      });

      trackAddToCart({
        page_type: 'product_detail',
        currency: 'USD',
        value: price,
        items: [
          {
            item_id: data.productId,
            item_name: data.title,
            item_category: data.categoryName || 'unknown',
            item_variant: 'course',
            price,
            quantity: 1,
          },
        ],
        course_id: data.productId,
      });
      list_cart.forEach((list) => {
        new_data.push(list);
      });
      new_data.push(newCart);
      setOpenModalCheckout(true);
    }

    const synchronizedCartData = new_data.map((item) => {
      return synchronizedSingleCartData({ item: item, isProduct: item.isProduct || false });
    });

    setCart(synchronizedCartData);
    localStorage.setItem('cart', JSON.stringify(synchronizedCartData));
  };

  const handleCollaps = async (currentSelected) => {
    if (currentSelect == currentSelected) {
      currentSelected = '';
    }
    SetCurrentSelect(currentSelected);
  };

  useEffect(() => {
    let cart = [];
    if (localStorage.getItem('cart')) {
      cart = JSON.parse(localStorage.getItem('cart'));
    }

    const newCart = cart.map((cartItem) => {
      return synchronizedSingleCartData({ item: cartItem, isProduct: cartItem.isProduct || false });
    });

    setCart(newCart);
  }, []);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="product" />
      </Head>

      {isLoading ? (
        <div className="loading">
          <Spinner animation="border" variant="white" style={{ width: '50px', height: '50px' }} />
        </div>
      ) : (
        ''
      )}

      <div className="course-description-breadcrumb">
        <Breadcrumb
          data={[
            { title: 'Home', url: '/' },
            { title: 'Product', url: '/category?products=all' },
            { title: truncateText(title), active: true },
          ]}
          variant="no-padding"
        />
      </div>
      <div className="image-container mobile w-100 d-lg-none d-block ">
        <img src={generateCoverImageUrl(image)} alt="course image" className="image w-100 h-100"></img>
      </div>

      <div className="course-description">
        <div className="course-description__overview">
          <Heading variety={2} className="d-lg-none d-block title-1 mb-3">
            {title}
          </Heading>
          {handleIsImageAvailable(provider?.logo) ? (
            <div className="user-image">
              <div className="user-image-container">
                <img src={currentImage} onError={() => handleImageToDefault()} />
              </div>
            </div>
          ) : (
            <div className="user-image">
              <p className="title-2">{provider?.name}</p>
              <div className="user-image-container">
                <img src={currentImage} onError={() => handleImageToDefault()} />
              </div>
            </div>
          )}
          <Heading variety={2} className="d-none d-lg-block title-1" style={{ wordBreak: 'break-word' }}>
            {title}
          </Heading>
          <div className="d-lg-none d-block border-bottom">
            <CourseData
              isProduct={true}
              discount={discount}
              courseOrganizer={provider?.code}
              price={price}
              productType={productType}
              totalPages={totalPages}
              readingTime={productReadingTime}
              language={productLanguage}
              center={center}
              releaseDate={productReleaseDate}
              referenceNumber={referenceNumber}
              isMobile
            />
          </div>
          <SubHeading variety={isMobile ? 2 : 3} className="title-2 mt-4 mt-lg-0">
            Product Description
          </SubHeading>
          <TextBody
            variety={isMobile ? 2 : 4}
            style={{ marginBottom: '6rem', wordBreak: 'break-word' }}
            className="descriptions"
            dangerouslySetInnerHTML={{ __html: description }}
          ></TextBody>

          {provider.information && provider.information !== '' ? (
            <Collapsible
              title={DETAIL_TITLE.PRODUCT_PROVIDER_INFORMATION}
              description={provider.information}
              isOpen={DETAIL_TITLE.PRODUCT_PROVIDER_INFORMATION === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {purchaseOption && purchaseOption !== '' ? (
            <Collapsible
              title={DETAIL_TITLE.OTHER_RELATED_PRODUCTS}
              description={purchaseOption}
              isOpen={DETAIL_TITLE.OTHER_RELATED_PRODUCTS === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}
        </div>
        <div className="course-description__recomended">
          <SubHeading variety={isMobile ? 2 : 3} className="title-2">{`Recommended Products`}</SubHeading>
          <div className="course-container-not-bundling p-2">
            {productRecommendation &&
              productRecommendation.slice(0, recommendedExpand ? productRecommendation.length : 2).map((data) => {
                const { _id, name, picture, discount, price, provider, description, slug } = data;
                return (
                  <RecommendedProducts
                    key={_id}
                    itemId={_id}
                    picture={picture}
                    provider={provider}
                    title={name}
                    description={description}
                    discount={discount}
                    price={price}
                    slug={slug}
                  />
                );
              })}
          </div>
          {productRecommendation?.length - 2 > 0 && (
            <SubHeading variety={4} onClick={() => setRecommendedExpand((prev) => !prev)} className="show-more">
              {recommendedExpand ? 'Show Less' : 'Show More'}
            </SubHeading>
          )}
        </div>
        <div className="course-description__checkout">
          <div className="card-floating description-checkout">
            <div className="image-container d-none d-lg-block">
              <img src={image ? image : '/dummy-img-video.png'} alt="course image" className="image"></img>
            </div>
            <div className="d-none d-lg-block">
              <CourseData
                isProduct={true}
                discount={discount}
                courseOrganizer={provider?.code}
                price={price}
                productType={productType}
                totalPages={totalPages}
                readingTime={productReadingTime}
                language={productLanguage}
                center={center}
                releaseDate={productReleaseDate}
                referenceNumber={referenceNumber}
              />
            </div>
            <Button
              variety={isMobile ? 3 : 4}
              fontType={FONT_TYPES.subHeading}
              type={isAddedToCart ? BUTTON_TYPE.addedToCart : BUTTON_TYPE.primary}
              onClick={() => handleAddToCart(productDetail)}
            >
              {isAddedToCart ? 'Remove from cart' : 'Add to cart'}
            </Button>
          </div>
        </div>
      </div>

      {openModalCheckout ? (
        <ModalCheckout
          setOpen={setOpenModalCheckout}
          open={openModalCheckout}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      ) : null}
    </>
  );
};

export default ProductDescription;
